import { FieldArray } from "formik";
import React, { useEffect, useState } from "react";

import { Button, FormControlLabel, Grid, IconButton, InputAdornment, Switch, TextField, Tooltip, Typography } from "@mui/material";
import makeStyles from '@mui/styles/makeStyles';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import DeleteIcon from '@mui/icons-material/Delete';

const useStyles = makeStyles(theme => ({
    container: {
        display: 'flex',
        flexWrap: 'wrap',
    },
    textField: {
        width: '85%',
        marginLeft: theme.spacing(1),
    },
    noteField: {
        width: '90%',
    },
    portlet: {
        'box-shadow': 'none',
    },
    formControl: {
        width: '85%',
        marginTop: theme.spacing(2),
        marginLeft: theme.spacing(1),
    },
    autoComplete: {
        width: '85%',
        marginLeft: theme.spacing(1),
    },
    button: {
        margin: theme.spacing(1)
    },
    paper: {
        marginTop: theme.spacing(2),
    },
    iconButton: {
        marginTop: theme.spacing(3),
    },
    clearButton: {
        marginTop: theme.spacing(3),
        marginLeft: '10px',
        color: 'rgba(0, 0, 0, 0.54)'
    },
    effectiveBox: {
        height: '100%',
        paddingTop: '25px'
    },
    tooltip: {
        backgroundColor: theme.palette.background.paper,
        color: theme.palette.text.primary,
        width: 400,
        border: `1px solid ${theme.palette.primary.main}`,
    },
    switch: {
        marginTop: theme.spacing(3),
        marginLeft: theme.spacing(1),
    },
}));

export default function ContractPricerStatsFieldArray({
    values, touched, errors, setFieldValue, disabled
}) {
    const TYPE_OPTIONS = ['INPATIENT', 'OUTPATIENT'];
    const classes = useStyles();

    function getError(key, index) {
        if (errors.contractPricerStatsConfigurationXRef) {
            if (errors.contractPricerStatsConfigurationXRef[index]) {
                return errors.contractPricerStatsConfigurationXRef[index][key];
            }
            return false;
        }
        return false;
    }

    function handleTypeChange(e, index) {
        setFieldValue(`contractPricerStatsConfigurationXRef.${index}.type`, e.target.value);
    }

    function handleNoteChange(e, index) {
        setFieldValue(`contractPricerStatsConfigurationXRef.${index}.note`, e.target.value);
    }

    function handleAlgorithmDescriptionChange(e, index) {
        setFieldValue(`contractPricerStatsConfigurationXRef.${index}.algorithmDescription`, e.target.value);
    }

    const getNextAvailableType = () => {
        const selected = values.contractPricerStatsConfigurationXRef.map(i => i.type);
        return TYPE_OPTIONS.find(t => !selected.includes(t)) || '';
    };

    const renderContractPricerStatsConfigurationXRef = (remove, insert) => {
        return (
            (<Grid item xs={12}>
                {values.contractPricerStatsConfigurationXRef.map((pricerStat, index) =>
                    <Grid container>
                        <Grid item xs={3}>
                            <TextField
                                select
                                required
                                label="Type"
                                disabled={disabled}
                                name={`contractPricerStatsConfigurationXRef.${index}.type`}
                                value={pricerStat.type || 'INPATIENT'}
                                onChange={e => handleTypeChange(e, index)}
                                className={classes.textField}
                                margin="normal"
                                helperText={getError('type', index)}
                                error={Boolean(getError('type', index))}
                                InputLabelProps={{
                                    shrink: true,
                                }}
                                SelectProps={{
                                    native: true,
                                }}
                            >
                                {TYPE_OPTIONS
                                    .filter(option => {
                                        // Allow current value, but block duplicates from other rows
                                        const selectedTypes = values.contractPricerStatsConfigurationXRef
                                            .map((item, i) => i !== index && item.type)
                                            .filter(Boolean);

                                        return option === pricerStat.type || !selectedTypes.includes(option);
                                    })
                                    .map(option => (
                                        <option key={option} value={option}>
                                            {option}
                                        </option>
                                    ))}
                            </TextField>
                        </Grid>


                        <Grid item xs={3}>
                            <TextField
                                required
                                disabled={disabled}
                                label="Algorithm Description"
                                name={`contractPricerStatsConfigurationXRef.${index}.algorithmDescription`}
                                className={classes.textField}
                                margin="normal"
                                value={pricerStat.algorithmDescription}
                                onChange={e => handleAlgorithmDescriptionChange(e, index)}
                                helperText={getError('algorithmDescription', index)}
                                error={Boolean(getError('algorithmDescription', index))}
                                multiline
                                rows={1}
                                inputProps={{ maxLength: 100 }}
                                InputLabelProps={{
                                    shrink: true,
                                }}
                            />
                        </Grid>

                        <Grid item xs={5}>
                            <TextField
                                required
                                disabled={disabled}
                                label="Note"
                                name={`contractPricerStatsConfigurationXRef.${index}.note`}
                                className={classes.noteField}
                                margin="normal"
                                value={pricerStat.note}
                                onChange={e => handleNoteChange(e, index)}
                                helperText={getError('note', index)}
                                error={Boolean(getError('note', index))}
                                multiline
                                rows={1}
                                inputProps={{ maxLength: 500 }}
                                InputLabelProps={{
                                    shrink: true,
                                }}
                            />
                        </Grid>
                        
                        <Grid item xs={1}>
                            <Grid container>
                            {(index >= 0 && values.contractPricerStatsConfigurationXRef.length > 1) && (
                                <IconButton
                                    edge="start"
                                    disabled={disabled}
                                    aria-label="Delete"
                                    onClick={() => {
                                        remove(index);
                                    }}
                                    className={classes.iconButton}
                                    size="large">
                                    <DeleteIcon />
                                </IconButton>
                            )}
                            {index === 0 && values.contractPricerStatsConfigurationXRef.length < TYPE_OPTIONS.length && (
                                <>
                                <IconButton
                                    edge="start"
                                    disabled={disabled}
                                    color="primary"
                                    aria-label="Add"
                                    onClick={() => insert(values.contractPricerStatsConfigurationXRef.length, {
                                        type: getNextAvailableType(),
                                        note: '',
                                        algorithmDescription: ''
                                    })}
                                    className={classes.iconButton}
                                    size="large">
                                    <AddCircleIcon />
                                </IconButton>
                                </>
                            )}
                            </Grid>
                        </Grid>

                    </Grid>
                )}
            </Grid>)
        );
    }

    return (
        <FieldArray
            name="contractPricerStatsConfigurationXRef"
            render={({ remove, insert }) => (
                <Grid container>
                    { renderContractPricerStatsConfigurationXRef(remove, insert) }
                </Grid>
            )}
        />
    );
}
